import { Grid, Select } from "@mantine/core";
import ReactApexChart from "react-apexcharts";
import { useMemo, useState } from "react";
import useSWR from "swr";
import AdminKpiCard from "../AdminKpiCard/AdminKpiCard";
import classes from "./AdminDashboardView.module.css";
import { buildDashboardSummaryQueryString, dashboardSwrKey } from "../../utils/dashboardQueryKeys";
import { DASHBOARD_EMPTY_STATE } from "../../constants";
import { fetcher } from "../../../../services/fetcher";

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
const chartBase = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: FONT },
  grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 2, right: 8 } },
  dataLabels: { enabled: false },
  tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: FONT } },
  legend: { fontSize: "12px", fontFamily: FONT },
};

const truncate = (s, n = 20) => { const str = String(s ?? ""); return str.length > n ? `${str.substring(0, n)}…` : str; };
const normalizeLabel = (s) => String(s ?? "").trim().toLowerCase();

// Operator tab palette for the column chart (cool blues + violets)
const DONUT_COLORS = ["#1d4ed8", "#0ea5e9", "#6366f1", "#a855f7", "#22c55e", "#f97316"];

const AdminOperatorTab = ({ filters = {}, timeFrame = "M", scope = "both" }) => {
  const [local, setLocal] = useState("Revenue %");

  const summaryCanon = buildDashboardSummaryQueryString({ timeFrame, scope, filters });
  const operatorOverviewUrl = dashboardSwrKey("dashboard/operator-overview-dashboard/", summaryCanon);
  const operatorDashboardUrl = dashboardSwrKey("dashboard/operator-dashboard/", summaryCanon);
  const topOperatorsContributionUrl = dashboardSwrKey("dashboard/top-10-operators/", summaryCanon);

  const { data: operatorOverviewData } = useSWR(operatorOverviewUrl, fetcher);
  const { data: operatorDashboardData } = useSWR(operatorDashboardUrl, fetcher);
  const { data: topOperatorsContributionData } = useSWR(topOperatorsContributionUrl, fetcher);

  const pick = (obj, keys) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return undefined;
  };

  const overviewSource =
    operatorOverviewData?.metrics ||
    operatorOverviewData?.kpis ||
    operatorOverviewData?.data?.metrics ||
    operatorOverviewData?.data ||
    operatorOverviewData ||
    {};
  const alertMetrics =
    operatorDashboardData?.alert_metrics ||
    operatorDashboardData?.data?.alert_metrics ||
    {};

  const dynamicOperatorKpis = [
    {
      label: "Active Operators",
      value: (() => {
        const v =
          pick(overviewSource, ["active_operators", "active_operator_count"]) ??
          alertMetrics?.total_active_operators?.count;
        return v != null ? String(v) : "—";
      })(),
      tone: "green",
    },
    {
      label: "Low Balance",
      value: (() => {
        const v =
          pick(overviewSource, ["low_wallet_operators", "low_balance_operators"]) ??
          alertMetrics?.operators_with_low_balance?.count;
        return v != null ? String(v) : "—";
      })(),
      tone: "red",
    },
    {
      label: "Inactive Rate",
      value: (() => {
        const v =
          alertMetrics?.inactive_rate?.value ??
          alertMetrics?.inactive_rate ??
          operatorDashboardData?.inactive_rate ??
          null;
        return v != null ? `${Number(v).toFixed(2)}%` : "—";
      })(),
      tone: "amber",
    },
    {
      label: "Onboarded",
      value: (() => {
        const v =
          alertMetrics?.onboarded_this_month?.count ??
          operatorDashboardData?.onboarded_this_month ??
          null;
        return v != null ? String(v) : "—";
      })(),
      tone: "blue",
    },
  ];

  const topOperatorsContributionRows = useMemo(() => {
    const d = topOperatorsContributionData;
    return Array.isArray(d?.top_operators)
      ? d.top_operators
      : Array.isArray(d?.operators)
        ? d.operators
        : Array.isArray(d?.results)
          ? d.results
          : Array.isArray(d)
            ? d
            : [];
  }, [topOperatorsContributionData]);

  const hasPoliciesMetric = topOperatorsContributionRows.some((item) =>
    pick(item, ["policies_count", "policy_count", "total_bookings", "bookings"]) != null
  );
  const effectiveLocal = local === "Policies" && !hasPoliciesMetric ? "Revenue %" : local;
  const mergedContributionRows = useMemo(() => {
    const map = new Map();
    topOperatorsContributionRows.forEach((item) => {
      const label = item?.operator_name || item?.name || "Operator";
      const key = normalizeLabel(label);
      const raw =
        effectiveLocal === "Policies"
          ? pick(item, ["policies_count", "policy_count", "total_bookings", "bookings"])
          : pick(item, ["percentage", "revenue_percent", "revenue_pct", "contribution_value"]);
      const value = Number(raw ?? 0);
      map.set(key, { label, value: (map.get(key)?.value ?? 0) + value });
    });
    return Array.from(map.values());
  }, [topOperatorsContributionRows, effectiveLocal]);

  const sortedContributionRows = [...mergedContributionRows].sort((a, b) => {
    const aVal = Number(
      a?.value ?? 0
    );
    const bVal = Number(
      b?.value ?? 0
    );
    return bVal - aVal;
  });
  const topOperatorRows = useMemo(
    () =>
      sortedContributionRows
        .filter((item) => normalizeLabel(item.label) !== "others")
        .slice(0, 10)
        .map((item) => ({ label: item.label, value: Number(item.value ?? 0) })),
    [sortedContributionRows]
  );
  const topOperatorCategories = topOperatorRows.map((item) => item.label);
  const topOperatorSeries = topOperatorRows.map((item) => item.value);

  return (
    <div className={classes.tabStack}>
      {/* KPIs */}
      <Grid gutter={12}>
        {dynamicOperatorKpis.map((item) => (
          <Grid.Col span={{ base: 6, md: 3 }} key={item.label}>
            <AdminKpiCard {...item} />
          </Grid.Col>
        ))}
      </Grid>

      {/* Top 10 Operators (column) */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 12 }}>
          <div className={classes.card}>
            <div className={classes.cardHeaderRow}>
              <span className={classes.cardHeader}>Top 10 Operators</span>
              <Select
                size="xs"
                className={classes.compactSelect}
                value={local}
                onChange={(value) => setLocal(value || "Revenue %")}
                data={[
                  { label: "Revenue %", value: "Revenue %" },
                  { label: "Policies", value: "Policies", disabled: !hasPoliciesMetric },
                ]}
              />
            </div>
            {topOperatorSeries.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  colors: DONUT_COLORS,
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%", distributed: true } },
                  legend: { show: false },
                  xaxis: {
                    categories: topOperatorCategories,
                    labels: {
                      formatter: (v) =>
                        effectiveLocal === "Policies"
                          ? Math.round(v).toLocaleString("en-IN")
                          : `${Number(v).toFixed(0)}%`,
                      style: { fontSize: "10px", fontFamily: FONT },
                    },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: {
                      formatter: (v) => truncate(v, 22),
                      style: { fontSize: "10px", fontFamily: FONT },
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: (v, { dataPointIndex }) => {
                        const raw = Number(v);
                        const fullLabel = topOperatorCategories[dataPointIndex] ?? "Operator";
                        return effectiveLocal === "Revenue %"
                          ? `${fullLabel}: ${raw.toFixed(2)}%`
                          : `${fullLabel}: ${raw.toLocaleString("en-IN")} policies`;
                      },
                    },
                  },
                }}
                series={[{ name: effectiveLocal === "Policies" ? "Policies" : "Revenue %", data: topOperatorSeries }]}
                type="bar"
                height={380}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

    </div>
  );
};

export default AdminOperatorTab;
